import { BUSINESS_CODE_PATTERN, normalizeBusinessCode } from '@pos/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { upsertBranch } from '../../data/mutators';
import type { Branch } from '../../types/domain';

const blank = (): Branch => ({ id: '', name: '', short: '' });

export function BranchFormModal({
  open,
  branch,
  onClose,
}: {
  open: boolean;
  branch: Branch | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Branch>(blank());

  useEffect(() => {
    if (open) setForm(branch ? { ...branch } : blank());
  }, [open, branch]);

  const mutation = useMutation({
    mutationFn: upsertBranch,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['branches'] });
      onClose();
    },
  });

  const set = <K extends keyof Branch>(k: K, v: Branch[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.short.trim()) return;
    mutation.mutate({ ...form, code: normalizeBusinessCode(form.code ?? '') });
  };

  return (
    <Modal
      open={open}
      title={branch ? 'แก้ไขสาขา' : 'เพิ่มสาขา'}
      description="ข้อมูลนี้จะแสดงในรายงาน สต๊อก และการตั้งค่าเครื่อง POS"
      onClose={onClose}
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Btn
            kind="default"
            type="button"
            className="w-full sm:w-auto"
            disabled={mutation.isPending}
            onClick={onClose}
          >
            ยกเลิก
          </Btn>
          <Btn
            kind="primary"
            type="submit"
            form="branch-settings-form"
            className="w-full sm:w-auto"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'กำลังบันทึก' : 'บันทึกสาขา'}
          </Btn>
        </div>
      }
    >
      <form
        id="branch-settings-form"
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
      >
        <Field label="รหัสสาขา">
          <Input
            aria-label="รหัสสาขา"
            value={form.code ?? ''}
            pattern={BUSINESS_CODE_PATTERN}
            maxLength={24}
            placeholder="BKK01"
            onChange={(e) => set('code', e.target.value.trim().toUpperCase())}
          />
        </Field>
        <Field label="ชื่อสาขา" required>
          <Input
            aria-label="ชื่อสาขา"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </Field>
        <Field label="ชื่อย่อ" required>
          <Input
            aria-label="ชื่อย่อ"
            value={form.short}
            onChange={(e) => set('short', e.target.value)}
          />
        </Field>
        {mutation.isError ? (
          <p role="alert" className="text-sm-label text-danger">
            {mutation.error.message}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
